import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { db } from '../../firebase/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  FaCheckCircle,
  FaSpinner,
  FaEnvelope,
} from 'react-icons/fa';
import './Join.css';

/* ── Tier Data ── */
const TIERS = [
  { id: 'student',   label: 'Student Member',   price: 'KES 500',    period: '/ year' },
  { id: 'associate', label: 'Associate Member',  price: 'KES 2,000',  period: '/ year' },
  { id: 'regular',   label: 'Regular Member',    price: 'KES 5,000',  period: '/ year' },
  { id: 'affiliate', label: 'Affiliate Member',  price: 'KES 3,000',  period: '/ year' },
  { id: 'corporate', label: 'Corporate Member',  price: 'KES 50,000', period: '/ year' },
  { id: 'honorary',  label: 'Honorary Member',   price: 'By Invitation', period: ''   },
];

/* ── Form Default State ── */
const EMPTY = {
  firstName:     '',
  lastName:      '',
  email:         '',
  phone:         '',
  organisation:  '',
  role:          '',
  qualification: '',
  motivation:    '',
  honeypot:      '',
};

/* ── Validation ── */
function validate(fields, tierId) {
  const errs = {};
  if (!tierId)
    errs.tier = 'Please select a membership tier.';
  if (!fields.firstName.trim())
    errs.firstName = 'First name is required.';
  if (!fields.lastName.trim())
    errs.lastName = 'Last name is required.';
  if (!fields.email.trim())
    errs.email = 'Email address is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email))
    errs.email = 'Enter a valid email address.';
  if (!fields.qualification.trim())
    errs.qualification = 'Please state your qualifications.';
  if (!fields.motivation.trim())
    errs.motivation = 'Please tell us why you want to join.';
  else if (fields.motivation.trim().length < 30)
    errs.motivation = 'Please write at least 30 characters.';
  return errs;
}


/* ── Next Steps ── */
const NEXT_STEPS = [
  {
    n:     '1',
    title: 'Submit form',
    text:  'Your email client opens with a pre-filled application.',
  },
  {
    n:     '2',
    title: 'NSK reviews',
    text:  'Our team reviews your credentials within 2 business days.',
  },
  {
    n:     '3',
    title: 'Fee payment',
    text:  'You\'ll receive payment instructions via M-Pesa or bank transfer.',
  },
  {
    n:     '4',
    title: 'Welcome to NSK',
    text:  'Your membership certificate and member access are activated.',
  },
];

/* ── Success State ── */
function SuccessState({ tierLabel, onReset }) {
  return (
    <div className="join-success" role="alert" aria-live="polite">
      <div className="join-success__icon" aria-hidden="true">
        <FaCheckCircle size={42} />
      </div>
      <h2 className="join-success__title">Application Submitted Successfully</h2>
      <p className="join-success__text">
        Thank you for applying for <strong>{tierLabel}</strong> membership.

        <br /><br />

        Your application has been submitted successfully and has been received by the
        Nuclear Society of Kenya. Our membership committee will review your application
        and contact you using the email address you provided within 2 business days
        regarding the outcome and any next steps, including membership payment if your
        application is approved.

        <br /><br />

        If you have any questions about your application, please contact us at{" "}
        <strong>info@nuclearsocietyofkenya.org</strong>.
      </p>
      <div className="join-success__actions">
        <Link to="/membership" className="btn btn--green">
          Back to Membership
        </Link>
        <button
          className="btn btn--outline"
          onClick={onReset}
          style={{
            color:       'var(--color-primary)',
            borderColor: 'var(--color-primary)',
          }}
        >
          Start Over
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   JOIN PAGE
══════════════════════════════════════════ */
function Join() {
  const [searchParams]        = useSearchParams();
  const [tierId,   setTierId] = useState('');
  const [fields,   setFields] = useState(EMPTY);
  const [errors,   setErrors] = useState({});
  const [status,   setStatus] = useState('idle');

  /* Pre-select tier from URL: /join?tier=regular */
  useEffect(() => {
    const param = searchParams.get('tier');
    if (param) {
      const match = TIERS.find(t => t.id === param.toLowerCase());
      if (match) setTierId(match.id);
    }
  }, [searchParams]);

  const selectedTier = TIERS.find(t => t.id === tierId) || null;

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    }
  }, [errors]);

  const handleTierSelect = useCallback((id) => {
    setTierId(id);
    if (errors.tier) {
      setErrors(prev => { const n = { ...prev }; delete n.tier; return n; });
    }
  }, [errors]);

  const handleSubmit = useCallback(async () => {
    if (fields.honeypot) return;

    const errs = validate(fields, tierId);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstKey = Object.keys(errs)[0];
      document.getElementById(`jf-${firstKey}`)?.focus();
      return;
    }

    setStatus('submitting');

try {
  await addDoc(collection(db, 'membershipApplications'), {
    tier: selectedTier.label,
    firstName: fields.firstName,
    lastName: fields.lastName,
    email: fields.email,
    phone: fields.phone,
    organisation: fields.organisation,
    role: fields.role,
    qualification: fields.qualification,
    motivation: fields.motivation,
    createdAt: serverTimestamp(),
  });

  
  setStatus('success');

} catch (error) {
  console.error(error);
  alert('Failed to submit application.');
  setStatus('idle');
}
  }, [fields, tierId, selectedTier]);

  const handleReset = useCallback(() => {
    setFields(EMPTY);
    setErrors({});
    setTierId('');
    setStatus('idle');
  }, []);

  return (
    <>
      {/* ── Hero ── */}
      <section className="join-hero" aria-labelledby="join-hero-heading">
        <div className="join-hero__inner">
          <nav className="join-hero__breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="join-hero__breadcrumb-sep" aria-hidden="true">›</span>
            <Link to="/membership">Membership</Link>
            <span className="join-hero__breadcrumb-sep" aria-hidden="true">›</span>
            <span>Apply</span>
          </nav>
          <h1 className="join-hero__title" id="join-hero-heading">
            Apply to Join NSK
          </h1>
          <p className="join-hero__lead">
            Complete the form below. Your details will open a pre-filled
            email to our membership team:- review it and hit Send.
            We respond within 2 business days.
          </p>
        </div>
      </section>

      {/* ── Body ── */}
      <section className="join-body" aria-label="Membership application">
        <div className="join-body__inner">

          {/* ── Sidebar ── */}
          <aside className="join-sidebar" aria-label="Membership tiers and next steps">

            {/* Tier picker */}
            <div className="join-sidebar__card">
              <h2 className="join-sidebar__heading">Select Your Tier</h2>
              {errors.tier && (
                <p
                  id="jf-tier"
                  role="alert"
                  className="join-tier-error"
                >
                  {errors.tier}
                </p>
              )}
              <div className="join-tier-list">
                {TIERS.map(({ id, label, price }) => (
                  <button
                    key={id}
                    type="button"
                    className={`join-tier-btn${tierId === id ? ' active' : ''}`}
                    onClick={() => handleTierSelect(id)}
                    aria-pressed={tierId === id}
                  >
                    <span>{label}</span>
                    <span className="join-tier-btn__price">{price}</span>
                  </button>
                ))}
              </div>
              <p className="join-tier-note">
                Not sure which tier fits you?{' '}
                <Link to="/membership" className="join-tier-note__link">
                  Compare tiers →
                </Link>
              </p>
            </div>

            {/* What happens next */}
            <div className="join-sidebar__card">
              <h2 className="join-sidebar__heading">What Happens Next</h2>
              <div className="join-sidebar__steps">
                {NEXT_STEPS.map(({ n, title, text }) => (
                  <div className="join-next-step" key={n}>
                    <div className="join-next-step__num" aria-hidden="true">
                      {n}
                    </div>
                    <p className="join-next-step__text">
                      <strong>{title}</strong>
                      {text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

          </aside>

          {/* ── Form Card ── */}
          <div className="join-form-card">
            {status === 'success' ? (
              <SuccessState
                tierLabel={selectedTier?.label}
                onReset={handleReset}
              />
            ) : (
              <>
                <h2 className="join-form-card__heading">
                  Membership Application
                </h2>
                <p className="join-form-card__sub">
                  Fields marked{' '}
                  <span aria-label="required" style={{ color: 'var(--color-danger)' }}>
                    *
                  </span>{' '}
                  are required.
                </p>

                {/* Selected tier banner */}
                {selectedTier && (
                  <div
                    className="join-tier-banner"
                    aria-label={`Selected tier: ${selectedTier.label}`}
                  >
                    <div className="join-tier-banner__info">
                      <span className="join-tier-banner__label">Selected Tier</span>
                      <span className="join-tier-banner__name">{selectedTier.label}</span>
                      <span className="join-tier-banner__price">
                        {selectedTier.price}
                        {selectedTier.period ? ` ${selectedTier.period}` : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="join-tier-banner__change"
                      onClick={() => setTierId('')}
                    >
                      Change tier
                    </button>
                  </div>
                )}

                <div
                  className="jform"
                  role="group"
                  aria-label="Membership application form"
                >
                  {/* Honeypot — hidden from real users, catches bots */}
                  <div className="jform__honeypot" aria-hidden="true">
                    <label htmlFor="jf-website">Website</label>
                    <input
                      id="jf-website"
                      name="honeypot"
                      type="text"
                      value={fields.honeypot}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Personal Details */}
                  <p className="jform__divider">Personal Details</p>

                  <div className="jform__row">
                    <div className="jform__field">
                      <label
                        htmlFor="jf-firstName"
                        className="jform__label jform__label--required"
                      >
                        First Name
                      </label>
                      <input
                        id="jf-firstName"
                        name="firstName"
                        type="text"
                        className={`jform__input${errors.firstName ? ' error' : ''}`}
                        value={fields.firstName}
                        onChange={handleChange}
                        autoComplete="given-name"
                        aria-required="true"
                        aria-describedby={errors.firstName ? 'err-firstName' : undefined}
                        aria-invalid={!!errors.firstName}
                      />
                      {errors.firstName && (
                        <span id="err-firstName" className="jform__error" role="alert">
                          {errors.firstName}
                        </span>
                      )}
                    </div>

                    <div className="jform__field">
                      <label
                        htmlFor="jf-lastName"
                        className="jform__label jform__label--required"
                      >
                        Last Name
                      </label>
                      <input
                        id="jf-lastName"
                        name="lastName"
                        type="text"
                        className={`jform__input${errors.lastName ? ' error' : ''}`}
                        value={fields.lastName}
                        onChange={handleChange}
                        autoComplete="family-name"
                        aria-required="true"
                        aria-describedby={errors.lastName ? 'err-lastName' : undefined}
                        aria-invalid={!!errors.lastName}
                      />
                      {errors.lastName && (
                        <span id="err-lastName" className="jform__error" role="alert">
                          {errors.lastName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="jform__row">
                    <div className="jform__field">
                      <label
                        htmlFor="jf-email"
                        className="jform__label jform__label--required"
                      >
                        Email Address
                      </label>
                      <input
                        id="jf-email"
                        name="email"
                        type="email"
                        className={`jform__input${errors.email ? ' error' : ''}`}
                        value={fields.email}
                        onChange={handleChange}
                        autoComplete="email"
                        aria-required="true"
                        aria-describedby={errors.email ? 'err-email' : undefined}
                        aria-invalid={!!errors.email}
                      />
                      {errors.email && (
                        <span id="err-email" className="jform__error" role="alert">
                          {errors.email}
                        </span>
                      )}
                    </div>

                    <div className="jform__field">
                      <label htmlFor="jf-phone" className="jform__label">
                        Phone Number
                      </label>
                      <input
                        id="jf-phone"
                        name="phone"
                        type="tel"
                        className="jform__input"
                        value={fields.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                        placeholder="+254 700 000 000"
                      />
                    </div>
                  </div>

                  {/* Professional Details */}
                  <p className="jform__divider">Professional Details</p>

                  <div className="jform__row">
                    <div className="jform__field">
                      <label htmlFor="jf-organisation" className="jform__label">
                        Organisation / Institution
                      </label>
                      <input
                        id="jf-organisation"
                        name="organisation"
                        type="text"
                        className="jform__input"
                        value={fields.organisation}
                        onChange={handleChange}
                        placeholder="e.g. University of Nairobi"
                      />
                    </div>

                    <div className="jform__field">
                      <label htmlFor="jf-role" className="jform__label">
                        Current Role / Title
                      </label>
                      <input
                        id="jf-role"
                        name="role"
                        type="text"
                        className="jform__input"
                        value={fields.role}
                        onChange={handleChange}
                        placeholder="e.g. Nuclear Engineering Student"
                      />
                    </div>
                  </div>

                  <div className="jform__field">
                    <label
                      htmlFor="jf-qualification"
                      className="jform__label jform__label--required"
                    >
                      Academic &amp; Professional Qualifications
                    </label>
                    <textarea
                      id="jf-qualification"
                      name="qualification"
                      className={`jform__textarea${errors.qualification ? ' error' : ''}`}
                      value={fields.qualification}
                      onChange={handleChange}
                      rows={4}
                      placeholder="e.g. BSc Nuclear Engineering (University of Nairobi, 2023), currently pursuing MSc…"
                      aria-required="true"
                      aria-describedby={
                        errors.qualification ? 'err-qualification' : 'hint-qualification'
                      }
                      aria-invalid={!!errors.qualification}
                    />
                    {errors.qualification ? (
                      <span
                        id="err-qualification"
                        className="jform__error"
                        role="alert"
                      >
                        {errors.qualification}
                      </span>
                    ) : (
                      <span id="hint-qualification" className="jform__hint">
                        List your relevant degrees, certifications, or professional registrations.
                      </span>
                    )}
                  </div>

                  {/* Motivation */}
                  <p className="jform__divider">Motivation</p>

                  <div className="jform__field">
                    <label
                      htmlFor="jf-motivation"
                      className="jform__label jform__label--required"
                    >
                      Why do you want to join NSK?
                    </label>
                    <textarea
                      id="jf-motivation"
                      name="motivation"
                      className={`jform__textarea${errors.motivation ? ' error' : ''}`}
                      value={fields.motivation}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us about your interest in nuclear science and what you hope to contribute to or gain from NSK…"
                      aria-required="true"
                      aria-describedby={errors.motivation ? 'err-motivation' : undefined}
                      aria-invalid={!!errors.motivation}
                    />
                    {errors.motivation && (
                      <span
                        id="err-motivation"
                        className="jform__error"
                        role="alert"
                      >
                        {errors.motivation}
                      </span>
                    )}
                  </div>

                  {/* Submit */}
                  <div className="jform__submit-row">
                    <p className="jform__privacy">
                      Your details are used only for your membership
                      application and kept confidential.
                    </p>
                    <button
                      type="button"
                      className="jform__submit"
                      onClick={handleSubmit}
                      disabled={status === 'submitting'}
                      aria-busy={status === 'submitting'}
                    >
                      {status === 'submitting' ? (
                        <>
                          <FaSpinner
                            size={14}
                            aria-hidden="true"
                            style={{ animation: 'spin 1s linear infinite' }}
                          />
                          Opening Email…
                        </>
                      ) : (
                        <>
                          <FaEnvelope size={14} aria-hidden="true" />
                          Submit Application
                        </>
                      )}
                    </button>
                  </div>

                </div>
              </>
            )}
          </div>

        </div>
      </section>
    </>
  );
}

export default Join;